import { useToast, useUser } from 'hooks';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
  FC,
  useMemo,
} from 'react';
import { Logger } from 'utils/logger';

interface WebSocketProviderProps {
  url: string;
  token?: string;
  onMessage?: (event: MessageEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (event: Event) => void;
  children: ReactNode;
}

interface WebSocketContextProps {
  isConnected: boolean;
  socketStatus: string;
  receivedMessage: string;
  sendMessage: (message: string) => void;
  showDownloadHandler: boolean;
  setShowDownloadHandler: (show: boolean) => void;
  downloadData: unknown[];
  setDownloadData: (data: unknown[]) => void;
  setReceivedMessage: (message: string) => void;
  scanLoading: boolean;
  setScanLoading: (loading: boolean) => void;
  setIsScanComplete: (complete: boolean) => void;
  isScanComplete: boolean;
  openScanInProgressModal: boolean;
  setOpenScanInProgressModal: (open: boolean) => void;
  disconnectWebSocket?: () => void;
}

const WebSocketContext = createContext<WebSocketContextProps | undefined>(
  undefined,
);

const RECONNECT_DELAY = 5000; // Delay between reconnection attempts

export const WebSocketProvider: FC<WebSocketProviderProps> = ({
  url,
  token,
  onMessage,
  onOpen,
  onClose,
  onError,
  children,
}) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [socketStatus, setSocketStatus] = useState<string>('');
  const [receivedMessage, setReceivedMessage] = useState<string>('');
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);

  // PDF DOWNLOAD
  const [showDownloadHandler, setShowDownloadHandler] = useState(false);
  const [downloadData, setDownloadData] = useState<unknown[]>([]);
  const [isScanComplete, setIsScanComplete] = useState(false);

  // BILLS SCAN
  const [scanLoading, setScanLoading] = useState(false);
  const [openScanInProgressModal, setOpenScanInProgressModal] = useState(false);

  // ACCOUNT INTEGRATION SYNC
  const [billMessages, setBillMessages] = useState<unknown[]>([]);
  const [poMessages, setPoMessages] = useState<unknown[]>([]);
  const [trackingCategorySyncMessages, setTrackingCategorySyncMessages] =
    useState<unknown[]>([]);

  const toast = useToast();

  let authToken = token || localStorage.getItem('token') || '';
  const { user } = useUser();

  // creates connection
  const connectWebSocket = useCallback(() => {
    if (!authToken) return;

    const urlWithToken = authToken
      ? `${url}?token=${encodeURIComponent(authToken)}`
      : url;

    Logger.log({
      message: '🚀 Attempting WebSocket connection...',
      enableInProduction: true,
    });

    // nosonar
    // if (socketRef.current) {
    //   Logger.log({
    //     message: '🔄 Closing previous WebSocket connection...',
    //     enableInProduction: true,
    //   });
    //   socketRef.current.close();
    // }

    socketRef.current = new WebSocket(urlWithToken);

    socketRef.current.onopen = () => {
      Logger.log({
        message: '✅ WebSocket connection established successfully!',
        enableInProduction: true,
      });
      setIsConnected(true);
      setSocketStatus('Connected');
      if (onOpen) onOpen();
    };

    socketRef.current.onmessage = (event: MessageEvent) => {
      const message = event.data;
      Logger.log({
        message: '📨 WebSocket message received:',
        data: message,
        enableInProduction: false,
      });

      try {
        const parsedMessage = JSON.parse(message);

        // Handle rate limit errors
        if (parsedMessage?.data?.rate_limit_error) {
          toast.toast.dismiss();
          toast.error(
            parsedMessage.data?.rate_limit_error || 'Rate limit reached',
          );
          setReceivedMessage(message);
          return;
        }

        // Handle AI Budget Parser messages
      } catch {
        Logger.error({
          message: '❌ Error parsing WS message:',
          data: message,
          enableInProduction: true,
        });
      }

      setReceivedMessage(message);
      onMessage?.(event);
    };

    socketRef.current.onclose = () => {
      Logger.log({
        message: '❌ WebSocket connection closed',
        enableInProduction: true,
      });
      setIsConnected(false);
      setSocketStatus('Disconnected');
      if (onClose) onClose();
      // Attempt to reconnect after a delay

      if (authToken) {
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
        }
        reconnectTimerRef.current = setTimeout(
          () => connectWebSocket(),
          RECONNECT_DELAY,
        );
      }
    };

    socketRef.current.onerror = (event: Event) => {
      Logger.log({
        message: '⚠️ WebSocket connection error:',
        data: event,
        enableInProduction: true,
      });
      if (onError) onError(event);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, authToken, onMessage, onOpen, onClose, onError]);

  useEffect(() => {
    if (!socketRef.current) {
      connectWebSocket();
    }

    return () => {
      if (socketRef?.current?.readyState === WebSocket.OPEN) {
        socketRef.current.close();
        Logger.log({
          message: '🔄 WebSocket connection closed.',
          enableInProduction: true,
        });
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const sendMessage = useCallback((message: string) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      Logger.log({
        message: '🚀 Sending message to WebSocket:',
        data: message,
      });
      socketRef.current.send(message);
    } else {
      Logger.log({
        message: '🚫 WebSocket is not open. Message not sent.',
        enableInProduction: true,
      });
    }
  }, []);

  const disconnectWebSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.onclose = null; // Prevent auto-reconnect
      socketRef.current.close();
      socketRef.current = null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      authToken = '';
      Logger.log({
        message: '🔄 WebSocket connection closed.',
        enableInProduction: true,
      });
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setIsConnected(false);
    setSocketStatus('Disconnected');
  }, []);

  const contextValue = useMemo(
    () => ({
      isConnected,
      socketStatus,
      receivedMessage,
      sendMessage,
      showDownloadHandler,
      setShowDownloadHandler,
      downloadData,
      setDownloadData,
      setReceivedMessage,
      scanLoading,
      setScanLoading,
      setIsScanComplete,
      isScanComplete,
      openScanInProgressModal,
      setOpenScanInProgressModal,
      billMessages,
      setBillMessages,
      poMessages,
      setPoMessages,
      trackingCategorySyncMessages,
      setTrackingCategorySyncMessages,
      disconnectWebSocket,
    }),
    [
      isConnected,
      socketStatus,
      receivedMessage,
      sendMessage,
      showDownloadHandler,
      setShowDownloadHandler,
      downloadData,
      setDownloadData,
      setReceivedMessage,
      scanLoading,
      setScanLoading,
      setIsScanComplete,
      isScanComplete,
      openScanInProgressModal,
      setOpenScanInProgressModal,
      billMessages,
      setBillMessages,
      poMessages,
      setPoMessages,
      trackingCategorySyncMessages,
      setTrackingCategorySyncMessages,
      disconnectWebSocket,
    ],
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
